import { Component, HostListener, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { addedPortOnLanSwitch, defaultCEUSerialPorts, defaultExternalCommunication } from '@models/system-setting';
import { PortDialogComponent } from '../port-dialog/port-dialog.component';
import { firstValueFrom, lastValueFrom, take } from 'rxjs';
import { getValidationErrors, mapErrors, ValidationErrorMap } from 'icreate/common/validators';
import { ceuValidator } from 'icreate/common/validators/ceu-unit/ceu-unit-validator';
import { addPrefixToErrors } from 'icreate/common/validators/custom-functions';
import { CommunicationType, communicationTypes } from 'icreate/common/models/fu-unit';
import { ExternalCommunication, ExternalCommunicationWithConfig } from 'icreate/common/models/external-communication';
import { ceuMaxNum, CeuUnit } from 'icreate/common/models/ceu-unit';
import _ from 'lodash';
import { Port } from 'icreate/common/models/port';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { TranslateService } from '@ngx-translate/core';

enum ExternalInputName {
    communicationType = 'communicationType',
    fieldBusLineIp = 'fieldBusLineIp',
}

@Component({
    selector: 'app-external-communication-dialog',
    templateUrl: './external-communication-dialog.component.html',
    styleUrls: ['./external-communication-dialog.component.css'],
    standalone: false,
})
export class ExternalCommunicationDialogComponent implements OnInit {
    readonly CONTEXT_MENU_TOP_OFFSET = -180;

    readonly CEU_MAX_NUM = ceuMaxNum;

    readonly SERIAL = communicationTypes.serial;
    readonly LAN = communicationTypes.lan;

    readonly COMMUNICATION_TYPE = ExternalInputName.communicationType;
    readonly FIELD_BUS_LINE_IP = ExternalInputName.fieldBusLineIp;

    externalCommunications: ExternalCommunicationWithConfig = defaultExternalCommunication;
    ceuList: CeuUnit[] = [];
    selectedCommunication: CeuUnit | ExternalCommunicationWithConfig | null = null;
    selectedPort: Port = null;
    isOptionOpen: boolean = false;
    isContextMenuOpen: boolean = false;
    validationErrors: ValidationErrorMap = {};
    selectedCeuIndexFromContextMenu: number = undefined;

    mainMessage: string = '';
    subMessage: string = '';

    changeBeforeDialogMainMessage: string = '';
    changeBeforeDialogSubMessage: string = '';

    contextMenuTop: number = 0;

    @ViewChild('confirmationDialog') confirmationDialog: ConfirmationWindowComponent;
    initDefaultTab: ExternalCommunicationWithConfig;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            fcuIndex: number;
            fcuId: string;
            vesselId: string;
            externalCommunication: ExternalCommunication;
            ceuList: CeuUnit[];
            save: boolean;
        },
        private dialog: MatDialog,
        private dialogRef: MatDialogRef<ExternalCommunicationDialogComponent>,
        private translate: TranslateService
    ) {}

    @HostListener('document:click', ['$event'])
    clickOut(event: MouseEvent) {
        this.isOptionOpen = false;
        this.isContextMenuOpen = false;
    }

    async ngOnInit() {
        this.externalCommunications = this.data.externalCommunication
            ? ({
                  ...this.data.externalCommunication,
                  commType: communicationTypes.serial,
                  isExternalCommunication: true,
              } as ExternalCommunicationWithConfig)
            : _.cloneDeep(defaultExternalCommunication);

        this.ceuList = this.data.ceuList ? _.cloneDeep(this.data.ceuList) : [];

        this.selectedCommunication = this.externalCommunications;
        this.initDefaultTab = _.cloneDeep(this.externalCommunications);
    }

    onInputChange(event: { name: string; value: string }) {
        const { name, value } = event;

        // if (this.FIELD_BUS_LINE_IP === name && !this.isSelectedExternalCommunication(this.selectedCommunication)) {
        //     this.selectedCommunication.fieldBusLineIp = value;
        // }

        this.checkInputErrors();
    }

    selectCommunication(selectedCommunication) {
        this.selectedCommunication = selectedCommunication;
    }

    setCommTypeChangeMessage() {
        if (this.selectedCommunication?.commType) {
            this.changeBeforeDialogMainMessage = this.translate.instant(
                'system-settings.external-communication.change.main-message',
                {
                    prevComm: this.selectedCommunication.commType,
                    nextComm:
                        this.selectedCommunication.commType === communicationTypes.lan
                            ? communicationTypes.serial
                            : communicationTypes.lan,
                }
            );
            this.changeBeforeDialogSubMessage = 'system-settings.external-communication.change.sub-message';
        }
    }

    isCommSerial(commType) {
        return commType === this.SERIAL;
    }

    openMoreOption(port) {
        this.selectedPort = port;
        this.isOptionOpen = true;
    }

    isSelectedExternalCommunication(
        selectedCommunication: CeuUnit | ExternalCommunicationWithConfig | null
    ): selectedCommunication is ExternalCommunicationWithConfig {
        return !!selectedCommunication && 'isExternalCommunication' in selectedCommunication;
    }

    selectInput(event: { name: string; value: string }) {
        const { name, value } = event;
        if (name === this.COMMUNICATION_TYPE) {
            const findIndex = this.ceuList.findIndex(ceu => ceu.index === this.selectedCommunication.index);

            let Ports: any = [...defaultCEUSerialPorts];

            if (value === this.LAN) {
                Ports = [...addedPortOnLanSwitch];
            }

            this.ceuList[findIndex] = {
                ...this.ceuList[findIndex],
                Ports: [...Ports],
                commType: value as CommunicationType,
            };
            this.selectedCommunication = this.ceuList[findIndex];
        }
    }

    hidePort(communicationIndex: number, portNo: number) {
        if (this.isSelectedExternalCommunication(this.selectedCommunication)) {
            this.externalCommunications.Ports = this.externalCommunications.Ports.map(port => {
                if (port.port === portNo) {
                    return {
                        ...port,
                        use: port.use === 1 ? 0 : 1,
                    };
                }
                return port;
            });
        } else {
            const findIndex = this.ceuList.findIndex(ceu => ceu.index === communicationIndex);
            this.ceuList[findIndex].Ports = this.ceuList[findIndex].Ports.map(port => {
                if (port.port === portNo) {
                    return {
                        ...port,
                        use: port.use === 1 ? 0 : 1,
                    };
                }
                return port;
            });
        }

        this.isOptionOpen = false;
    }

    addCommunication() {
        // this.ceuList.push({
        //     ...structuredClone(newCommunicationData),
        //     index: this.ceuList.length + 1,
        //     vesselId: '',
        //     fcuId: '',
        //     id: '',
        // });
    }

    async editPort() {
        const newDialogRef = this.dialog.open(PortDialogComponent, {
            data: {
                title: this.isSelectedExternalCommunication(this.selectedCommunication) ? 'Default' : '',
                use: this.selectedPort.use,
                port: this.selectedPort.port,
                commName: this.selectedPort.name,
                commType: this.selectedPort.type,
            },
            disableClose: true,
        });

        const dialogResult = await firstValueFrom(newDialogRef.afterClosed());

        if (dialogResult) {
            if (!this.isSelectedExternalCommunication(this.selectedCommunication)) {
                const findCommunicationIndex = this.ceuList.findIndex(
                    ceu => ceu.index === this.selectedCommunication.index
                );
                const findPortIndex = this.ceuList[findCommunicationIndex].Ports.findIndex(
                    port => port.port === this.selectedPort.port
                );
                this.ceuList[findCommunicationIndex].Ports[findPortIndex] = dialogResult;
            } else {
                const findPortIndex = this.externalCommunications.Ports.findIndex(
                    port => port.port === this.selectedPort.port
                );
                this.externalCommunications.Ports[findPortIndex] = dialogResult;
            }
        }

        this.isOptionOpen = false;
    }

    close() {
        this.dialogRef.close();
    }

    checkInputErrors() {
        let errors = [];

        for (let i = 0; i < this.ceuList.length; i++) {
            const ceuErrors = getValidationErrors(ceuValidator(), {
                index: this.ceuList[i].index,
                commType: this.ceuList[i].commType,
                lineAIp: this.ceuList[i].lineAIp,
                lineBIp: this.ceuList[i].lineBIp,
                Ports: this.ceuList[i].Ports,
            });

            const prefixedCeuErros = addPrefixToErrors(ceuErrors, String(this.ceuList[i].index));

            errors = [...errors, ...prefixedCeuErros];
        }

        this.validationErrors = mapErrors(errors);
    }

    save() {
        this.checkInputErrors();
        if (this.isSaveButtonDisabled()) return;
        this.dialogRef.close({
            externalCommunication: this.externalCommunications,
            ceuList: this.ceuList,
        });
    }

    isSaveButtonDisabled() {
        return (
            (_.isEqual(this.data.ceuList, this.ceuList) &&
                _.isEqual(this.initDefaultTab.Ports, this.externalCommunications.Ports)) ||
            !!Object.keys(this.validationErrors).length
        );
    }

    async deleteCeu(ceuIndex: number) {
        try {
            let indexToDelete = this.ceuList.findIndex(ceu => ceu.index === ceuIndex);
            if (indexToDelete !== -1) {
                this.mainMessage = this.translate.instant(
                    'system-settings.external-communication.delete.main-message',
                    {
                        index: indexToDelete + 1,
                    }
                );
                this.subMessage = 'system-settings.external-communication.delete.sub-message';

                this.confirmationDialog.setVisible(true);
                const option = await lastValueFrom(this.confirmationDialog.onOptionSelected.pipe(take(1)));

                if (option === ConfirmationWindowTypes.SAVE) {
                    this.ceuList.splice(indexToDelete, 1);
                    this.ceuList = this.ceuList.map((ceu, index) => ({
                        ...ceu,
                        index: index + 1,
                    }));

                    if (this.ceuList.length !== 0) {
                        this.selectedCommunication = this.ceuList[0];
                    } else {
                        this.selectedCommunication = this.externalCommunications;
                    }
                }
            }

            this.isContextMenuOpen = false;
            this.selectedCeuIndexFromContextMenu = undefined;
        } catch (error) {
            console.log(error);
        }
    }

    copyCeu(copiedCeu: CeuUnit) {
        this.ceuList.push({
            ...copiedCeu,
            index: this.ceuList.length + 1,
            id: '',
        });

        this.isContextMenuOpen = false;
        this.selectedCeuIndexFromContextMenu = undefined;
    }

    openContextMenu(event: MouseEvent, index: number) {
        event.preventDefault();
        const currentTargetRect = (event.currentTarget as HTMLElement).getBoundingClientRect();
        this.contextMenuTop = currentTargetRect.y + this.CONTEXT_MENU_TOP_OFFSET;

        this.isContextMenuOpen = true;
        this.selectedCeuIndexFromContextMenu = index;
    }

    scrollSideMenu() {
        this.isContextMenuOpen = false;
    }
}
